import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule} from '@angular/platform-browser';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { of, catchError } from 'rxjs';


import { NgChartsModule } from 'ng2-charts';
import { NgxPermissionsModule } from 'ngx-permissions';
import { JwtModule } from './jwt/angular-jwt.module';
import { JWT_OPTIONS } from './jwt/jwtoptions.token';
import { IConfig, NgxMaskDirective, NgxMaskPipe, provideEnvironmentNgxMask } from 'ngx-mask';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { AppState } from './store/app.state';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TRANSLATE_HTTP_LOADER_CONFIG } from '@ngx-translate/http-loader';

import { registerLocaleData } from '@angular/common';
import localeHu from '@angular/common/locales/hu';
import localeEn from '@angular/common/locales/en';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';

import { AppRoutingModule } from './app-routing.module';

const MY_DATE_FORMATS = {
  parse: { dateInput: 'input' },
  display: {
    dateInput: 'input',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM yyyy'
  }
};


// Services
import {
    StateService,
    AppInitService,
    AuthService
} from './services';
import { NavigationHelper } from './helpers/navigation.helper';

import {
  EmployeeNamePipe,
  EmployeeDisplayNamePipe,
  EmployeeEmailPipe,
  EmployeeTypePipe,
  EmployeeReviewStatePipe,
  UserDisplayNamePipe,
  EmployeeIdentifierPipe,  
  NoZeroPipe,
  ReviewPeriodTypePipe,
  AssessmentStatusPipe,
  PeriodTypeDatePipe,
  ReviewPeriodNamePipe,
  RoleDisplayNamePipe,
  AssessmentStatusNamePipe,
  PlainDatePipe,
  LocalizedDatePipe
} from './pipes';

// Components
import { 
    AppComponent,    
    HeaderComponent,
    SubHeaderComponent,
    MenuComponent,
    ContactDetailsComponent,
    EmployeePositionComponent,
    ReviewPeriodComponent,
    ReviewPeriodEditComponent,
    EmployeeSearchComponent,    
    EmployeeGroupComponent,
    EmployeeHierarchyComponent,
    LeaderComponent,
    ChangePasswordComponent,
    EmployeeReviewPeriodComponent,
    MissingReviewsComponent,
    GoalEditComponent,    
    GoalActionEditComponent,
    GoalActionQuartersComponent,    
    QuestionComponent,
    ProductGuideComponent,
    PageProgressComponent,
    GroupGoalsComponent,
    GroupGoalFieldComponent,    
    GroupGoalEditComponent,
    UserRolesComponent,
    BackNavComponent,
    GoalKpiEditComponent,
    LegendComponent,
    MultiItemSelectorComponent,
    SystemComponent,
    CompletedReviewsComponent,
    TextEditPanelComponent,
    EmployeeListComponent,
    DashboardIndicatorGroupComponent,
    AssessmentListItemComponent,
    AssessmentListItemDetailsComponent,
    CommentSectionComponent,
    PositionProfilesComponent,
    PositionProfileItemComponent,
    PositionQuestionsComponent,
    PositionDataComponent,
    EmployeeAndPositionSearchComponent,
    WeightIndicatorComponent,
    GoalViewComponent,
    GoalPanelComponent,
    GoalChecklistComponent,
    GoalCustomGoalsComponent,
    GoalCompetencyResultSelectorComponent,
    GroupGoalViewComponent,
    ReviewPeriodCustomGoalsComponent,
    GoalPeriodStatusStepperComponent,
    UserLanguageComponent,
    AuditLineComponent
  } from './components';

// Dialogs 
import {
  EmployeeGroupAddComponent,
  UserBulkInsertComponent,
  EmployeeReviewDialogComponent,
  ActionForReviewPeriodDialogComponent,
  CompanyUserRoleDialogComponent,
  KpiTypeAddDialogComponent,
  ErrorDetailsDialogComponent,
  AboutEmployeeAddDialogComponent,
  CompanyEditDialogComponent,
  EmailAdminDialogComponent,
  UploadDialogComponent,
  CommentDialogComponent,
  GoalPeriodStateChangeDialogComponent,
  ReviewPeriodCustomGoalDialogComponent,
} from './dialogs';

// Pages
import {     
    AssessmentComponent,
    AssessmentsComponent,
    ConfirmEmailComponent,
    CreateRoleUserComponent,
    GoalsComponent,
    LoginComponent,    
    ReportsComponent,
    EmployeeComponent,
    EmployeeAddComponent,
    SettingsComponent,
    PositionComponent,
    PasswordResetComponent,
    CompanySelectorComponent,
    SimpleSearchTestComponent,
    CancelUserDeleteComponent,
    PrivacyPolicyComponent,
    GoalBasicSettingsComponent,
    CompanyPageComponent,
    TextBasedGoalComponent,
    NumericGoalComponent,
    GroupGoalComponent,
    TestPageComponent
 } from './pages';

 // Reports
 import {
    ReviewDateComponent,
    FreetextPanelComponent,
    LegendCardComponent,

    ReportComponent,
    PersonalityTestDetailsComponent,
    PersonalityTestDiagramComponent,
    ReportFrameComponent,
    TeamDysfunctionsComponent,
    TeamStressMapComponent,
    PositionProfileReportComponent,
    CategoryResultDistributionTableComponent,
 } from './reports';

import { SharedModule } from './shared/shared.module';
import { TokenService } from './shared/services';

import { LoaderInterceptor } from './interceptors/loader.interceptor';
import { LanguageInterceptor } from './interceptors/language.interceptor';
import { environment } from 'src/environments/environment';
import { NgxGaugeModule } from 'ngx-gauge';
import { DatePipe } from '@angular/common';
import { LanguageService } from './services/language.service';
import { I18nDateAdapter } from './adapters/I18nDate.adapter';



  export const options: Partial<IConfig> | (() => Partial<IConfig>) = null;

  export function appInit(appInitService: AppInitService) {
      return () => {      
        return appInitService.load().toPromise();
      }
  }
  
  export function userInit(authService: AuthService, appState: AppState) {
    return () => {      
      // If we are in an iframe and it's not a reload, we should wait for the SSO process
      // to handle the token. This prevents loading the 'previous' user's data.
      const isIframe = NavigationHelper.isIframe();
      if (isIframe) {
          appState.iframeLoginMode.value = true;
          
          if (!NavigationHelper.isReload()) {
            console.log('SSO: Delaying initial user load (fresh navigation in iframe)');
            return of(null).toPromise();
          }
      }

      return authService.loadUser().pipe(
        catchError(error => {        
          authService.logOut();
          return of(null);
        })
      ).toPromise();
    }
  }

  export function translationInit(languageService: LanguageService) {
    return () => languageService.initializeLanguage();
  }

export function jwtOptionsFactory(tokenService: TokenService, stateService: StateService) {
    return {
      tokenGetter: () => {
        return tokenService.getToken();
      },
      allowedDomains: () => {        
        const host = new URL(stateService.state.config.rest_end_point).host;
        return [host];
      }
    };
}

export function tokenGetter() {
  return localStorage.getItem("access_token");
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader();
}

// Lokális adatok regisztrálása
registerLocaleData(localeHu, 'hu');
registerLocaleData(localeEn, 'en');

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,

    SharedModule,
    AppRoutingModule,
    NgChartsModule,
    NgxGaugeModule,
    NgxMaskDirective,
    NgxMaskPipe,
    NgxPermissionsModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
    }),
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory,
        deps: [TokenService, StateService]
      }
    })
  ],
  providers: [
      {
        provide: APP_INITIALIZER,
        useFactory: translationInit,
        multi: true,
        deps: [LanguageService]
      },
      {
        provide: APP_INITIALIZER,
        useFactory: userInit,
        multi: true,
        deps: [AuthService, AppState]
        },
        {
          provide: APP_INITIALIZER,
          useFactory: appInit,
          multi: true,
          deps: [AppInitService]
        },
        provideEnvironmentNgxMask(),
        {
          provide: HTTP_INTERCEPTORS,
          useClass: LoaderInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: LanguageInterceptor,
          multi: true
        },
        {
          provide: TRANSLATE_HTTP_LOADER_CONFIG,
          useValue: {
            prefix: './assets/i18n/',
            suffix: '.json'
          }
        },
        { 
          provide: DateAdapter, 
          useClass: I18nDateAdapter 
        },
        { 
          provide: MAT_DATE_FORMATS, 
          useValue: MY_DATE_FORMATS 
        },
        {
          provide: LOCALE_ID,
          deps: [LanguageService],
          useFactory: (langService: LanguageService) => langService.getCurrentLanguage()
        },
        {
          provide: MAT_DATE_LOCALE,
          deps: [LanguageService],
          useFactory: (langService: LanguageService) => langService.getCurrentLanguage()
        },
        DatePipe
  ],
  declarations: [

    // Pipes
    EmployeeNamePipe,
    EmployeeDisplayNamePipe,
    EmployeeEmailPipe,
    EmployeeTypePipe,
    UserDisplayNamePipe,
    RoleDisplayNamePipe,
    EmployeeReviewStatePipe,
    EmployeeIdentifierPipe,
    ReviewPeriodTypePipe,
    ReviewPeriodNamePipe,
    PeriodTypeDatePipe,
    AssessmentStatusPipe,
    AssessmentStatusNamePipe,
    NoZeroPipe,
    PlainDatePipe,
    LocalizedDatePipe,

    //Dialogs
    EmployeeGroupAddComponent,
    UserBulkInsertComponent,
    EmployeeReviewDialogComponent,
    ActionForReviewPeriodDialogComponent,
    ErrorDetailsDialogComponent, 
    AboutEmployeeAddDialogComponent,
    KpiTypeAddDialogComponent, 
    CompanyUserRoleDialogComponent, 
    CompanyEditDialogComponent,
    EmailAdminDialogComponent,
    UploadDialogComponent,
    CommentDialogComponent,
    GoalPeriodStateChangeDialogComponent,
    ReviewPeriodCustomGoalDialogComponent,

    //Public
    AppComponent,
    LoginComponent,
    PasswordResetComponent,
    CancelUserDeleteComponent,

    //Common
    ReviewPeriodComponent,
    EmployeeSearchComponent,
    ReportFrameComponent, 
    PersonalityTestDiagramComponent, 
    PersonalityTestDetailsComponent,  
    PositionProfileReportComponent,
    BackNavComponent,
    LegendComponent, 
    MultiItemSelectorComponent, 
    PageProgressComponent, 
    TextEditPanelComponent,
    AuditLineComponent,

    //LoggedIn Users
    MenuComponent,
    HeaderComponent,
    SubHeaderComponent,
    CompanySelectorComponent,
    ConfirmEmailComponent,
    ReportComponent,
    ReportsComponent,

    //LoggedIn Assessment
    AssessmentsComponent,
    AssessmentListItemComponent,
    AssessmentListItemDetailsComponent,
    AssessmentComponent,
    QuestionComponent, 
    GoalBasicSettingsComponent,
    ProductGuideComponent, 
    FreetextPanelComponent,
    LegendCardComponent,
    CategoryResultDistributionTableComponent,

    //LoggedIn - Employees
    EmployeeComponent,
    ContactDetailsComponent,
    PositionComponent,
    UserRolesComponent, 
    EmployeeAddComponent,
    EmployeeHierarchyComponent,
    EmployeePositionComponent,
    CreateRoleUserComponent,
    LeaderComponent,
    ChangePasswordComponent,
    UserLanguageComponent,

    //LoggedIn - Goals
    GoalsComponent,
    GoalViewComponent,
    GoalEditComponent,
    GoalPanelComponent,
    GoalChecklistComponent,
    GoalActionEditComponent,
    GoalActionQuartersComponent,    
    GroupGoalsComponent, 
    GroupGoalFieldComponent, 
    GroupGoalViewComponent,
    GroupGoalEditComponent,
    GoalKpiEditComponent,
    GoalCompetencyResultSelectorComponent,
    GoalCustomGoalsComponent,
    TextBasedGoalComponent,
    NumericGoalComponent,
    GroupGoalComponent,
    GoalPeriodStatusStepperComponent,
    
    CommentSectionComponent,
    PrivacyPolicyComponent,
    
    //Admin
    ReviewPeriodEditComponent, 
    ReviewPeriodCustomGoalsComponent,
    SystemComponent,
    EmployeeGroupComponent,
    EmployeeGroupAddComponent,
    EmployeeReviewPeriodComponent,  
    MissingReviewsComponent,
    CompletedReviewsComponent,
    SettingsComponent,
    EmployeeListComponent,
    DashboardIndicatorGroupComponent,
    CompanyPageComponent,

    PositionDataComponent,
    PositionProfilesComponent,
    PositionProfileItemComponent,
    PositionQuestionsComponent,

    TeamDysfunctionsComponent, 
    TeamStressMapComponent,
    WeightIndicatorComponent,
    
    //--
    SimpleSearchTestComponent, 
    ReviewDateComponent, 
    EmployeeAndPositionSearchComponent,
    TestPageComponent
  ],
  bootstrap: [
    AppComponent
  ]
})
export class AppModule {   
}
